import { Entry } from "contentful";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";

import Button from "~/components/common/button";
import { useServerSideProps } from "~/contexts/server-side-props";
import useScript from "~/hooks/use-script";
import { FaqServerSideData } from "~/shared/help-centre/faq-sitemap.interface";
import { assertEditorialType } from "~/utils/editorial-utils";

type Props = {
  entry: Entry<unknown>;
};

export default function SmartTribuneFaq(props: Props) {
  assertEditorialType<unknown>(props.entry, "smartTribuneFaq");

  const router = useRouter();
  const { serverSideProps } = useServerSideProps();
  const [path, setPath] = useState(router.asPath);
  const markup = useRef<HTMLDivElement>(null);

  const faq: FaqServerSideData | undefined = serverSideProps.faq;

  const lang = useMemo(() => router.locale?.split("-")[0] || "en", [router.locale]);
  const faqScripts: Record<string, string> = {
    _default: process.env.NEXT_PUBLIC_SMART_TRIBUNE_FAQ_JS_DEFAULT_URL!,
    ar: process.env.NEXT_PUBLIC_SMART_TRIBUNE_FAQ_JS_ARABIC_URL!,
  };

  useEffect(() => {
    if (!faq || !markup.current) {
      return;
    }

    const scripts = Array.from(markup.current.querySelectorAll("script"))
      .filter((script) => !script.hasAttribute("async"))
      .map((script) => {
        const executable = document.createElement("script");

        for (const { name, value } of Array.from(script.attributes)) {
          executable.setAttribute(name, value);
        }

        executable.async = false;
        executable.text = script.text;

        return executable;
      });

    scripts.forEach((script) => document.body.appendChild(script));

    return () => scripts.forEach((script) => script.remove());
  }, [faq]);

  useEffect(() => {
    if (faq) {
      return;
    }

    setPath(router.asPath);
    if (router.asPath !== path && path !== undefined) {
      router.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, router.asPath]);

  const initUrl = process.env.NEXT_PUBLIC_SMART_TRIBUNE_FAQ_JS_URL!.replace("{locale}", router.locale!);

  useScript(faq ? undefined : initUrl, true, true);
  const faqLoaded = useScript(faq ? undefined : faqScripts[lang] || faqScripts._default, true, true);
  const guideLoaded = useScript(faq ? undefined : process.env.NEXT_PUBLIC_SMART_TRIBUNE_GUIDE_JS_URL, true, true);

  return (
    <>
      {faq ? (
        <>
          <Head>
            {faq.assets.stylesheet && <link rel="stylesheet" href={faq.assets.stylesheet} />}
            {faq.assets.scripts.map((src) => (
              <link key={src} rel="preload" as="script" href={src} />
            ))}
          </Head>
          <div ref={markup} dangerouslySetInnerHTML={{ __html: faq.html }} />
        </>
      ) : (
        <>
          <div id="st-faq" style={{ minHeight: "100px" }}>
            {(!faqLoaded || !guideLoaded) && <Button loading style={{ cursor: "default" }} />}
          </div>
          <div id="st-guide-standalone" />
        </>
      )}
    </>
  );
}
